/**
 * Login / claim (ARCHITECTURE.md §8, §10) — a complete page, not a stub.
 *
 * Single email-code flow that auto-detects instance state: an *unclaimed*
 * instance shows the first-run **claim** flow (become the sole owner); a claimed
 * instance shows **sign in**. Both are two steps — request a code, then verify —
 * and the server always answers requests 200 to avoid enumeration (§10). Verify
 * failures collapse to one generic message.
 *
 * The hero is the safety posture itself: the instance "ships disarmed", stated
 * up front, because that is the most characteristic thing about this system.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { cn } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import { api, unwrap } from "../lib/api";
import { queryClient, sessionQuery } from "../lib/query";
import { keys } from "../lib/queryKeys";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

type Step = "email" | "code";

function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const session = useQuery(sessionQuery);
  const claimed = session.data?.claimed ?? true;

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const requestCode = useMutation({
    mutationFn: async (addr: string) => {
      const body = { json: { email: addr } };
      return claimed
        ? unwrap(await api.auth.login.request.$post(body))
        : unwrap(await api.auth.claim.request.$post(body));
    },
    onSuccess: () => setStep("code"),
  });

  const verifyCode = useMutation({
    mutationFn: async (input: { email: string; code: string }) => {
      const body = { json: input };
      return claimed
        ? unwrap(await api.auth.login.verify.$post(body))
        : unwrap(await api.auth.claim.verify.$post(body));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.auth.session() });
      await navigate({ to: "/" });
    },
  });

  function onRequest(e: FormEvent) {
    e.preventDefault();
    if (email.trim().length === 0) return;
    requestCode.mutate(email.trim());
  }

  function onVerify(e: FormEvent) {
    e.preventDefault();
    if (code.trim().length === 0) return;
    verifyCode.mutate({ email: email.trim(), code: code.trim() });
  }

  const title = claimed ? t("auth.signIn") : t("auth.claimTitle");
  const subtitle = claimed ? t("auth.signInSubtitle") : t("auth.claimSubtitle");

  return (
    <div className="flex min-h-full items-center justify-center bg-page px-6 py-12">
      <div className="w-full max-w-sm">
        {/* Wordmark */}
        <div className="mb-8 flex items-center gap-2">
          <span className="font-mono text-lg font-semibold tracking-[0.22em] text-ink">
            rich<span className="text-brand">rich</span>
          </span>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.16em] text-muted">
            {t("auth.tradingConsole")}
          </span>
        </div>

        <div className="rounded-panel border border-hairline bg-surface p-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">
            {claimed ? t("auth.ownerAccess") : t("auth.firstRun")}
          </div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-ink">{title}</h1>
          <p className="mt-1.5 text-sm text-ink-secondary">{subtitle}</p>

          {step === "email" ? (
            <form onSubmit={onRequest} className="mt-5 flex flex-col gap-3">
              <Field
                label={t("auth.emailLabel")}
                type="email"
                autoComplete="email"
                value={email}
                onChange={setEmail}
                placeholder={t("auth.emailPlaceholder")}
                autoFocus
              />
              {requestCode.isError ? <FormError message={t("auth.requestCodeError")} /> : null}
              <SubmitButton pending={requestCode.isPending}>{t("auth.sendCode")}</SubmitButton>
            </form>
          ) : (
            <form onSubmit={onVerify} className="mt-5 flex flex-col gap-3">
              <div className="text-xs text-ink-secondary">
                {t("auth.codeSentPrefix")} <span className="font-mono text-ink">{email}</span>{" "}
                {t("auth.codeSentSuffix")}
              </div>
              <Field
                label={t("auth.verificationCodeLabel")}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={setCode}
                placeholder={t("auth.codePlaceholder")}
                mono
                autoFocus
              />
              {verifyCode.isError ? <FormError message={t("auth.verifyError")} /> : null}
              <SubmitButton pending={verifyCode.isPending}>
                {claimed ? t("auth.signIn") : t("auth.claimButton")}
              </SubmitButton>
              <button
                type="button"
                onClick={() => {
                  setCode("");
                  setStep("email");
                }}
                className="text-xs text-muted transition-colors hover:text-ink"
              >
                {t("auth.useDifferentEmail")}
              </button>
            </form>
          )}
        </div>

        {/* Hero note: the safety posture. */}
        <p className="mt-5 flex items-start gap-2 text-xs text-muted">
          <span aria-hidden className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-good" />
          {t("auth.shipsDisarmed")}
        </p>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: "numeric" | "text";
  mono?: boolean;
  autoFocus?: boolean;
}

function Field({ label, value, onChange, mono = false, ...input }: FieldProps) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-ink-secondary">{label}</span>
      <input
        {...input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink",
          "placeholder:text-muted focus:border-brand focus:outline-none",
          mono && "font-mono tracking-[0.2em]",
        )}
      />
    </label>
  );
}

function SubmitButton({ pending, children }: { pending: boolean; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <button
      type="submit"
      disabled={pending}
      className={cn(
        "mt-1 w-full rounded-md bg-brand px-3 py-2 text-sm font-medium text-white",
        "transition-opacity hover:opacity-90 disabled:opacity-60",
      )}
    >
      {pending ? t("auth.working") : children}
    </button>
  );
}

function FormError({ message }: { message: string }) {
  return (
    <p role="alert" className="text-xs text-critical">
      {message}
    </p>
  );
}
